// src/contexts/WalletContext.js
import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { Wallet as EthersWallet, ethers } from 'ethers';
import { createSecureProvider } from '../utils/secureProvider';
import { NetworkContext } from './NetworkContext';
import { AuthContext } from './AuthContext';
import { getAutoPassword, generateSecurePassword } from '../utils/cryptoUtils';
import { 
  generateMnemonic, 
  validateMnemonic, 
  getWalletFromMnemonic, 
  deriveMultipleWallets,
  isPrivateKey 
} from '../utils/mnemonicUtils';
// Import secure memory utilities
import {
  createSecureReference,
  getSecureData,
  secureClearData,
  createTemporaryAccess
} from '../utils/secureMemoryUtils';
// Import the translation hook
import { useTranslation } from 'react-i18next';

export const WalletContext = createContext(null);

// Storage key for encrypted wallets
const ENCRYPTED_WALLETS_KEY = 'bitdog_encrypted_wallets';
// Legacy key for unencrypted wallets
const LEGACY_WALLETS_KEY = 'bitdogWallets';
// Key for session active state
const SESSION_KEY = 'bitdog_session_active';
// Key for storing seed phrase
const ENCRYPTED_SEED_PHRASE_KEY = 'bitdog_encrypted_seed_phrase';
// Key for storing selected wallet address
const SELECTED_WALLET_KEY = 'bitdog_selected_wallet';

export const WalletProvider = ({ children }) => {
  const { t } = useTranslation(); // Add translation hook
  const { currentChain } = useContext(NetworkContext);
  const { 
    isAuthenticated, 
    hasPassword, 
    hasAutoPassword,
    encryptData, 
    decryptData,
    sessionActive,
    createAutoPassword
  } = useContext(AuthContext);
  
  const [userWallets, setUserWallets] = useState([]);
  const [currentWallet, setCurrentWallet] = useState(null);
  // SECURITY IMPROVEMENT: Instead of storing seedPhrase directly in state,
  // we'll use a secure reference token
  const [seedPhraseToken, setSeedPhraseToken] = useState(null);
  const [hasSeedPhrase, setHasSeedPhrase] = useState(false);
  
  const [provider, setProvider] = useState(
    currentChain?.rpcUrl ? createSecureProvider(currentChain.rpcUrl) : null
  );
  const [address, setAddress] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [isEncrypted, setIsEncrypted] = useState(false);
  const [lastPassword, setLastPassword] = useState(null); // For temporary operations
  const [autoPassword, setAutoPassword] = useState(null); // For auto-generated password
  const [hasStoredWallets, setHasStoredWallets] = useState(false);
  const [autoDecryptAttempted, setAutoDecryptAttempted] = useState(false);
  
  // Error handling states
  const [decryptionError, setDecryptionError] = useState(null);
  const [isDecrypting, setIsDecrypting] = useState(false);
  
  // Track previous authentication state to prevent redundant clears
  // This must be at component level, not inside useEffect
  const prevAuthRef = useRef(false);

  // Cleanup function to securely clear sensitive data
  const clearSensitiveData = () => {
    if (seedPhraseToken) {
      secureClearData(seedPhraseToken);
      setSeedPhraseToken(null);
    }
    
    // Clear lastPassword
    if (lastPassword) {
      // Overwrite in memory before clearing
      setLastPassword('0'.repeat(lastPassword.length));
      setLastPassword(null);
    }
  };

  // Clean up sensitive data when component unmounts or auth state changes
  useEffect(() => {
    return () => {
      clearSensitiveData();
    };
  }, []);

  // Check if we have stored wallets and seed phrase on component mount
  useEffect(() => {
    const checkForStoredData = () => {
      const encryptedData = localStorage.getItem(ENCRYPTED_WALLETS_KEY);
      const legacyData = localStorage.getItem(LEGACY_WALLETS_KEY);
      const encryptedSeedPhrase = localStorage.getItem(ENCRYPTED_SEED_PHRASE_KEY);
      
      // Update wallet state based on what's in storage
      if (encryptedData) {
        setIsEncrypted(true);
        setHasStoredWallets(true);
        console.log("Found encrypted wallet data in storage:", encryptedData.substring(0, 20) + "...");
      } else if (legacyData) {
        setIsEncrypted(false); // Explicitly set as unencrypted
        setHasStoredWallets(true);
        console.log("Found legacy wallet data in storage");
      } else {
        setIsEncrypted(false); // Explicitly set as unencrypted
        setHasStoredWallets(false);
        console.log("No wallet data found in storage");
      }
      
      // Check for seed phrase
      if (encryptedSeedPhrase) {
        setHasSeedPhrase(true);
        console.log("Found encrypted seed phrase in storage");
      } else {
        setHasSeedPhrase(false);
        console.log("No seed phrase found in storage");
      }
    };
    
    checkForStoredData();
  }, []); // Only run once on mount, no dependencies

  /**
   * Secure pattern for temporarily storing the seed phrase
   * @param {string} seedPhrase - The seed phrase to store securely
   * @param {number} timeoutMs - Time until auto-deletion (default: 3 minutes)
   */
  const secureStoreSeedPhrase = (seedPhrase, timeoutMs = 3 * 60 * 1000) => {
    // Clear any existing token first
    if (seedPhraseToken) {
      secureClearData(seedPhraseToken);
    }
    
    if (!seedPhrase) {
      setSeedPhraseToken(null);
      return;
    }
    
    // Create a new secure reference
    const token = createSecureReference(seedPhrase, timeoutMs);
    setSeedPhraseToken(token);
  };

  /**
   * Get the effective password for wallet operations
   * UPDATED: Simplified to directly use the auto-password and handle emergency creation
   * @returns {Promise<string>} - Effective password for encryption/decryption
   */
  const getEffectivePassword = async () => {
    // If we already have the auto password cached, use it
    if (autoPassword) {
      return autoPassword;
    }
    
    try {
      // Get the auto password - now passing lastPassword if available
      const effectivePassword = await getAutoPassword(lastPassword);
      // Cache it for future operations
      setAutoPassword(effectivePassword);
      return effectivePassword;
    } catch (error) {
      console.error('Failed to get effective password:', error);
      
      // EMERGENCY FIX: If no auto password exists, create one
      if (error.message.includes('not found')) {
        console.warn("No auto password found - creating emergency auto password");
        try {
          // Create a new auto password - passing lastPassword if available
          const { success, password } = await createAutoPassword(lastPassword);
          if (success && password) {
            console.log("Created emergency auto password successfully");
            setAutoPassword(password);
            return password;
          }
        } catch (createError) {
          console.error("Failed to create emergency auto password:", createError);
        }
      }
      
      throw new Error(t('wallet.errors.unlockFailed', { message: error.message }));
    }
  };

  /**
   * Attempts to auto-decrypt wallets and seed phrase during initialization if session is active
   * UPDATED: To also handle seed phrase decryption
   */
  const attemptAutoDecrypt = async () => {
    try {
      // This is critical: we need to check the most up-to-date session status
      const activeSession = localStorage.getItem(SESSION_KEY) === 'true';
      
      if (!activeSession) {
        console.log("No active session found, skipping auto-decrypt");
        return false;
      }
      
      console.log("Active session found, attempting to auto-decrypt wallet");
      
      const encryptedData = localStorage.getItem(ENCRYPTED_WALLETS_KEY);
      if (!encryptedData) {
        console.log("No encrypted wallet data found");
        return false;
      }
      
      setIsDecrypting(true);
      
      // Get the auto-password
      const effectivePassword = await getEffectivePassword();
      
      // Decrypt wallets
      const decrypted = await decryptData(encryptedData, effectivePassword);
      const wallets = JSON.parse(decrypted);
      
      if (Array.isArray(wallets) && wallets.length > 0) {
        console.log(`Auto-decrypt successful: loaded ${wallets.length} wallets`);
        setUserWallets(wallets);
        
        // Try to restore the previously selected wallet
        const savedWalletAddress = localStorage.getItem(SELECTED_WALLET_KEY);
        if (savedWalletAddress) {
          const savedWallet = wallets.find(w => 
            w.address && w.address.toLowerCase() === savedWalletAddress.toLowerCase()
          );
          
          if (savedWallet) {
            console.log(`Restored previously selected wallet: ${savedWallet.address}`);
            setCurrentWallet(savedWallet);
          } else {
            console.log(`Previously selected wallet not found, using first wallet`);
            setCurrentWallet(wallets[0]);
          }
        } else {
          setCurrentWallet(wallets[0]);
        }
        
        // Now try to decrypt the seed phrase if it exists
        const encryptedSeedPhrase = localStorage.getItem(ENCRYPTED_SEED_PHRASE_KEY);
        if (encryptedSeedPhrase) {
          try {
            const decryptedSeedPhrase = await decryptData(encryptedSeedPhrase, effectivePassword);
            // SECURITY IMPROVEMENT: Store securely instead of in state
            secureStoreSeedPhrase(decryptedSeedPhrase);
            console.log("Seed phrase successfully decrypted and securely stored");
          } catch (seedError) {
            console.error("Failed to decrypt seed phrase:", seedError);
            // Continue anyway - we have the wallets
          }
        }
        
        return true;
      } else {
        console.log("Auto-decrypt failed: invalid wallet data format");
        return false;
      }
    } catch (error) {
      console.log("Auto-decrypt failed:", error.message);
      return false;
    } finally {
      setIsDecrypting(false);
    }
  };

  // This effect specifically handles the initial loading and auto-decryption
  useEffect(() => {
    // Only run once after component mounts
    if (isInitialized || autoDecryptAttempted) return;
    
    const initializeWallets = async () => {
      console.log("Initializing wallet context...");
      setAutoDecryptAttempted(true);
      
      try {
        // Try auto-decrypt first if there's an active session
        const autoDecryptSuccess = await attemptAutoDecrypt();
        
        if (autoDecryptSuccess) {
          console.log("Auto-decrypt successful, wallet loaded");
        } else {
          console.log("Auto-decrypt was not successful, will wait for authentication");
          
          // If we have legacy unencrypted wallets, load those
          const legacy = localStorage.getItem(LEGACY_WALLETS_KEY);
          if (legacy) {
            try {
              const arr = JSON.parse(legacy);
              if (Array.isArray(arr) && arr.length > 0) {
                console.log(`Loaded ${arr.length} legacy unencrypted wallets`);
                setUserWallets(arr);
                
                // Try to restore the previously selected wallet
                const savedWalletAddress = localStorage.getItem(SELECTED_WALLET_KEY);
                if (savedWalletAddress) {
                  const savedWallet = arr.find(w => 
                    w.address && w.address.toLowerCase() === savedWalletAddress.toLowerCase()
                  );
                  
                  if (savedWallet) {
                    console.log(`Restored previously selected wallet: ${savedWallet.address}`);
                    setCurrentWallet(savedWallet);
                  } else {
                    console.log(`Previously selected wallet not found, using first wallet`);
                    setCurrentWallet(arr[0]);
                  }
                } else {
                  setCurrentWallet(arr[0]);
                }
              }
            } catch (err) {
              console.error('Failed to parse legacy wallets:', err);
            }
          }
        }
      } catch (error) {
        console.error("Error during wallet initialization:", error);
      } finally {
        // Mark as initialized
        setIsInitialized(true);
      }
    };
    
    initializeWallets();
  }, [isInitialized, autoDecryptAttempted, decryptData]);

  // This effect is for handling decryption with a password after authentication
  useEffect(() => {
    // Only run if we're initialized
    if (!isInitialized) return;
    
    const decryptWallets = async () => {
      // Skip if user wallets are already loaded
      if (userWallets.length > 0) return;
      
      const encryptedData = localStorage.getItem(ENCRYPTED_WALLETS_KEY);
      if (!encryptedData) return;
      
      setIsDecrypting(true);
      setDecryptionError(null);
      
      try {
        console.log("Attempting to decrypt wallet after authentication");
        
        // Get effective password for decryption
        const effectivePassword = await getEffectivePassword();
        
        const decrypted = await decryptData(encryptedData, effectivePassword);
        const wallets = JSON.parse(decrypted);
        
        if (Array.isArray(wallets) && wallets.length > 0) {
          console.log(`Successfully loaded ${wallets.length} encrypted wallets`);
          setUserWallets(wallets);
          
          // Try to restore the previously selected wallet
          const savedWalletAddress = localStorage.getItem(SELECTED_WALLET_KEY);
          if (savedWalletAddress) {
            const savedWallet = wallets.find(w => 
              w.address && w.address.toLowerCase() === savedWalletAddress.toLowerCase()
            );
            
            if (savedWallet) {
              console.log(`Restored previously selected wallet: ${savedWallet.address}`);
              setCurrentWallet(savedWallet);
            } else {
              console.log(`Previously selected wallet not found, using first wallet`);
              setCurrentWallet(wallets[0]);
            }
          } else {
            setCurrentWallet(wallets[0]);
          }
          
          // IMPORTANT: Always try to decrypt seed phrase whenever authentication changes
          await loadAndDecryptSeedPhrase();
        } else {
          throw new Error(t('wallet.errors.invalidWalletFormat'));
        }
      } catch (error) {
        console.error('Failed to decrypt wallets:', error);
        setDecryptionError(t('wallet.errors.unlockFailed', { message: error.message }));
      } finally {
        setIsDecrypting(false);
      }
    };
    
    // If authenticated, decrypt
    if (isAuthenticated) {
      decryptWallets();
    }
    
    // Only clear wallets if authentication was previously true and is now false
    // This prevents clearing during initial loading or when creating a new wallet
    else if (!isAuthenticated && prevAuthRef.current === true) {
      console.log("Authentication lost after being authenticated, clearing wallet data from memory");
      setUserWallets([]);
      setCurrentWallet(null);
      // SECURITY IMPROVEMENT: Securely clear the seed phrase
      if (seedPhraseToken) {
        secureClearData(seedPhraseToken);
        setSeedPhraseToken(null);
      }
    }
    
    // Update the ref for next check
    prevAuthRef.current = isAuthenticated;
    
  }, [isInitialized, isAuthenticated, decryptData, userWallets.length, t]);

  // Add this new effect to save the selected wallet when it changes
  useEffect(() => {
    // Only save if we have a current wallet with an address
    if (currentWallet?.address) {
      try {
        localStorage.setItem(SELECTED_WALLET_KEY, currentWallet.address);
        console.log(`Saved selected wallet: ${currentWallet.address}`);
      } catch (error) {
        console.error('Error saving wallet selection:', error);
      }
    }
  }, [currentWallet]);

  // Add this new effect to ensure wallets and seed phrase are saved immediately when created
  useEffect(() => {
    const saveNewWallets = async () => {
      // Only run if we're authenticated, have wallets
      if (!isAuthenticated || userWallets.length === 0) return;
      
      try {
        console.log("Saving newly created wallets to secure storage");
        const walletsJson = JSON.stringify(userWallets);
        
        // Get effective password - ensure we have it available
        let effectivePassword;
        try {
          effectivePassword = await getEffectivePassword();
        } catch (error) {
          console.warn("Failed to get effective password, will create one:", error);
          // Try to get auto-password from context
          const { success, password } = await createAutoPassword();
          if (!success || !password) {
            throw new Error(t('wallet.errors.unlockFailed', { message: "Failed to create auto password" }));
          }
          setAutoPassword(password);
          effectivePassword = password;
        }
        
        if (!effectivePassword) {
          throw new Error(t('wallet.errors.unlockFailed', { message: "No effective password available for encryption" }));
        }
        
        // Encrypt and save wallets immediately
        const encrypted = await encryptData(walletsJson, effectivePassword);
        localStorage.setItem(ENCRYPTED_WALLETS_KEY, encrypted);
        setIsEncrypted(true);
        
        // Also save seed phrase if we have one
        if (seedPhraseToken) {
          const seedPhrase = getSecureData(seedPhraseToken, true);
          if (seedPhrase) {
            const encryptedSeedPhrase = await encryptData(seedPhrase, effectivePassword);
            localStorage.setItem(ENCRYPTED_SEED_PHRASE_KEY, encryptedSeedPhrase);
            console.log("Seed phrase saved to secure storage");
          }
        }
        
        // Remove legacy storage if exists
        localStorage.removeItem(LEGACY_WALLETS_KEY);
        
        console.log(`Successfully saved ${userWallets.length} wallets to secure storage`);
      } catch (error) {
        console.error("Failed to save new wallets:", error);
        // Fallback to unencrypted storage temporarily
        localStorage.setItem(LEGACY_WALLETS_KEY, JSON.stringify(userWallets));
      }
    };
    
    // If we just added a wallet, save it immediately
    if (isAuthenticated && userWallets.length > 0) {
      saveNewWallets();
    }
  }, [isAuthenticated, userWallets, seedPhraseToken, createAutoPassword, encryptData, t]);

  // Monitor session status directly to handle refresh cases
  useEffect(() => {
    // Check for session changes
    const checkSession = () => {
      const activeSession = localStorage.getItem(SESSION_KEY) === 'true';
      
      console.log("Session active check:", activeSession);
      
      // If session is active but we have no wallets loaded, try auto-decrypt
      if (activeSession && userWallets.length === 0 && hasStoredWallets) {
        console.log("Active session but no wallets loaded, attempting auto-decrypt");
        attemptAutoDecrypt();
      }
      // If session was ended and we have wallets loaded, clear them
      else if (!activeSession && userWallets.length > 0) {
        console.log("Session is not active, clearing loaded wallets");
        setUserWallets([]);
        setCurrentWallet(null);
        // SECURITY IMPROVEMENT: Securely clear the seed phrase
        if (seedPhraseToken) {
          secureClearData(seedPhraseToken);
          setSeedPhraseToken(null);
        }
      }
    };
    
    // Run initially
    checkSession();
    
    // Also set up event listener for storage changes
    const handleStorageChange = (event) => {
      if (event.key === SESSION_KEY) {
        console.log("Session status changed:", event.newValue);
        checkSession();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [userWallets.length, hasStoredWallets]);

  // Whenever userWallets changes and we're encrypted, save them encrypted
  useEffect(() => {
    const saveWallets = async () => {
      if (!isInitialized || !hasAutoPassword) return;
      
      try {
        // Only save if we have wallets to save
        if (userWallets.length === 0) return;
        
        const walletsJson = JSON.stringify(userWallets);
        
        // Get effective password for encryption
        const effectivePassword = await getEffectivePassword();
        
        // Encrypt and save wallets
        const encrypted = await encryptData(walletsJson, effectivePassword);
        localStorage.setItem(ENCRYPTED_WALLETS_KEY, encrypted);
        setIsEncrypted(true);
        
        // Also save seed phrase if we have one
        if (seedPhraseToken) {
          const seedPhrase = getSecureData(seedPhraseToken, true);
          if (seedPhrase) {
            const encryptedSeedPhrase = await encryptData(seedPhrase, effectivePassword);
            localStorage.setItem(ENCRYPTED_SEED_PHRASE_KEY, encryptedSeedPhrase);
          }
        }
        
        // Remove legacy storage if exists
        localStorage.removeItem(LEGACY_WALLETS_KEY);
        
        console.log(`Saved ${userWallets.length} encrypted wallets`);
      } catch (error) {
        console.error('Failed to save encrypted wallets:', error);
      }
    };
    
    if (isEncrypted) {
      saveWallets();
    } else {
      // If not encrypted, save unencrypted (legacy mode)
      if (isInitialized && userWallets.length > 0) {
        localStorage.setItem(LEGACY_WALLETS_KEY, JSON.stringify(userWallets));
      }
    }
  }, [userWallets, isInitialized, hasAutoPassword, isEncrypted, encryptData, seedPhraseToken]);

  // Whenever currentChain or currentWallet changes, set up the provider
  useEffect(() => {
    if (currentChain?.rpcUrl) {
      try {
        const p = createSecureProvider(currentChain.rpcUrl);
        setProvider(p);
    
        if (currentWallet?.privateKey) {
          const w = new EthersWallet(currentWallet.privateKey, p);
          setAddress(w.address);
        } else {
          setAddress('');
        }
      } catch (error) {
        console.error("Error creating secure provider:", error);
      }
    }
  }, [currentChain, currentWallet]);


/**
 * Attempt to load and decrypt the seed phrase
 * This will be called whenever wallets are loaded or session is restored
 */
const loadAndDecryptSeedPhrase = async () => {
  try {
    const encryptedSeedPhrase = localStorage.getItem(ENCRYPTED_SEED_PHRASE_KEY);
    if (!encryptedSeedPhrase) {
      console.log("No encrypted seed phrase found in storage");
      return null;
    }

    // Get the effective password for decryption
    const effectivePassword = await getEffectivePassword();
    
    // Decrypt the seed phrase
    const decryptedSeedPhrase = await decryptData(encryptedSeedPhrase, effectivePassword);
    console.log("Seed phrase successfully decrypted");
    
    // SECURITY IMPROVEMENT: Store securely instead of in state
    secureStoreSeedPhrase(decryptedSeedPhrase);
    setHasSeedPhrase(true);
    
    return decryptedSeedPhrase;
  } catch (error) {
    console.error("Failed to decrypt seed phrase:", error);
    return null;
  }
};

// MODIFICATION: Enhance retryWalletDecryption to explicitly handle seed phrase
const retryWalletDecryption = async () => {
  setIsDecrypting(true);
  setDecryptionError(null);
  
  try {
    // Check for encrypted data
    const encryptedData = localStorage.getItem(ENCRYPTED_WALLETS_KEY);
    const legacyData = localStorage.getItem(LEGACY_WALLETS_KEY);
    
    // If no data at all, we can't do anything
    if (!encryptedData && !legacyData) {
      throw new Error(t('wallet.errors.noWalletData'));
    }
    
    // First try encrypted data
    if (encryptedData) {
      try {
        console.log("Attempting to decrypt wallet data");
        
        // Get effective password for decryption
        const effectivePassword = await getEffectivePassword();
        
        const decrypted = await decryptData(encryptedData, effectivePassword);
        const wallets = JSON.parse(decrypted);
        
        if (Array.isArray(wallets) && wallets.length > 0) {
          console.log(`Successfully decrypted ${wallets.length} wallets on retry`);
          setUserWallets(wallets);
          
          // Try to restore the previously selected wallet
          const savedWalletAddress = localStorage.getItem(SELECTED_WALLET_KEY);
          if (savedWalletAddress) {
            const savedWallet = wallets.find(w => 
              w.address && w.address.toLowerCase() === savedWalletAddress.toLowerCase()
            );
            
            if (savedWallet) {
              console.log(`Restored previously selected wallet: ${savedWallet.address}`);
              setCurrentWallet(savedWallet);
            } else {
              console.log(`Previously selected wallet not found, using first wallet`);
              setCurrentWallet(wallets[0]);
            }
          } else {
            setCurrentWallet(wallets[0]);
          }
          
          // Update state to reflect encryption
          setIsEncrypted(true);
          
          // IMPORTANT: Always attempt to load the seed phrase whenever wallets are loaded
          await loadAndDecryptSeedPhrase();
          
          // Clear any previous errors
          setDecryptionError(null);
          return true;
        } else {
          throw new Error(t('wallet.errors.invalidWalletFormat'));
        }
      } catch (encryptError) {
        console.error('Error decrypting wallet:', encryptError);
        
        // If we have legacy data, fall back to it
        if (legacyData) {
          console.log("Falling back to legacy wallet data");
          throw new Error(t('wallet.errors.decryptFallback')); 
        } else {
          throw encryptError; // Re-throw if no fallback available
        }
      }
    }
    
    // If we get here, try legacy data
    if (legacyData) {
      try {
        console.log("Loading legacy wallet data");
        const wallets = JSON.parse(legacyData);
        
        if (Array.isArray(wallets) && wallets.length > 0) {
          console.log(`Successfully loaded ${wallets.length} wallets from legacy storage`);
          setUserWallets(wallets);
          
          // Try to restore the previously selected wallet
          const savedWalletAddress = localStorage.getItem(SELECTED_WALLET_KEY);
          if (savedWalletAddress) {
            const savedWallet = wallets.find(w => 
              w.address && w.address.toLowerCase() === savedWalletAddress.toLowerCase()
            );
            
            if (savedWallet) {
              console.log(`Restored previously selected wallet: ${savedWallet.address}`);
              setCurrentWallet(savedWallet);
            } else {
              console.log(`Previously selected wallet not found, using first wallet`);
              setCurrentWallet(wallets[0]);
            }
          } else {
            setCurrentWallet(wallets[0]);
          }
          
          // Update state to reflect no encryption
          setIsEncrypted(false);
          
          // Clear any previous errors
          setDecryptionError(null);
          
          // Now try to encrypt them for next time
          try {
            const effectivePassword = await getEffectivePassword();
            const walletsJson = JSON.stringify(wallets);
            const encrypted = await encryptData(walletsJson, effectivePassword);
            localStorage.setItem(ENCRYPTED_WALLETS_KEY, encrypted);
            setIsEncrypted(true);
            console.log("Successfully migrated legacy wallet data to encrypted storage");
          } catch (migrateError) {
            console.error("Failed to migrate to encrypted storage:", migrateError);
            // Continue with unencrypted wallets
          }
          
          return true;
        } else {
          throw new Error(t('wallet.errors.invalidWalletFormat'));
        }
      } catch (legacyError) {
        console.error('Error loading legacy wallet data:', legacyError);
        throw legacyError;
      }
    }
    
    // If we get here, both approaches failed
    throw new Error(t('wallet.errors.loadFailed'));
    
  } catch (error) {
    console.error('Retry wallet decryption failed:', error);
    setDecryptionError(t('wallet.errors.unlockFailed', { message: error.message }));
    return false;
  } finally {
    setIsDecrypting(false);
  }
};
  
  /**
   * Clear decryption error state
   */
  const clearDecryptionError = () => {
    setDecryptionError(null);
  };


/**
 * Migrate from unencrypted to encrypted storage
 * UPDATED: Simplified to use auto-password only
 * @param {Array} wallets - Wallet array to encrypt
 */
const migrateToEncryptedStorage = async (wallets) => {
  try {
    console.log("Starting migration to encrypted storage...");
    const walletsJson = JSON.stringify(wallets);
    
    // Get effective password for encryption
    const effectivePassword = await getEffectivePassword();
    
    const encrypted = await encryptData(walletsJson, effectivePassword);
    localStorage.setItem(ENCRYPTED_WALLETS_KEY, encrypted);
    
    // Also encrypt seed phrase if available
    if (seedPhraseToken) {
      const seedPhrase = getSecureData(seedPhraseToken, true);
      if (seedPhrase) {
        const encryptedSeedPhrase = await encryptData(seedPhrase, effectivePassword);
        localStorage.setItem(ENCRYPTED_SEED_PHRASE_KEY, encryptedSeedPhrase);
      }
    }
    
    localStorage.removeItem(LEGACY_WALLETS_KEY);
    setIsEncrypted(true);
    console.log('Successfully migrated to encrypted storage');
    
    // Update the state directly - create a new array reference to trigger renders
    setUserWallets(prevWallets => [...prevWallets]);
  } catch (error) {
    console.error('Failed to migrate to encrypted storage:', error);
  }
};

/**
 * Force encryption of wallets
 * UPDATED: Simplified to use auto-password only
 * @returns {Promise<boolean>} - Success status
 */
const forceEncryptWallets = async () => {
  if (userWallets.length === 0) return false;
  
  try {
    console.log("Forcing wallet encryption");
    
    // Simply encrypt with the current wallet state
    const walletsJson = JSON.stringify(userWallets);
    
    // Get effective password for encryption
    const effectivePassword = await getEffectivePassword();
    
    const encrypted = await encryptData(walletsJson, effectivePassword);
    localStorage.setItem(ENCRYPTED_WALLETS_KEY, encrypted);
    
    // Also encrypt seed phrase if available
    if (seedPhraseToken) {
      const seedPhrase = getSecureData(seedPhraseToken, true);
      if (seedPhrase) {
        const encryptedSeedPhrase = await encryptData(seedPhrase, effectivePassword);
        localStorage.setItem(ENCRYPTED_SEED_PHRASE_KEY, encryptedSeedPhrase);
      }
    }
    
    localStorage.removeItem(LEGACY_WALLETS_KEY);
    setIsEncrypted(true);
    
    console.log("Wallets successfully encrypted");
    
    // Update state to trigger re-renders
    setUserWallets(prevWallets => [...prevWallets]);
    
    return true;
  } catch (error) {
    console.error("Failed to encrypt wallets:", error);
    return false;
  }
};

  /**
   * Set the password for wallet operations (temporary storage)
   * UPDATED: Called for backward compatibility but not needed
   * @param {string} password - Password to use for encryption/decryption
   */
  const setWalletPassword = (password) => {
    setLastPassword(password);
    // No longer need to clear auto password since it's always available
  };

/**
 * Create a new wallet using seed phrase
 * @returns {Promise<Object>} - New wallet object and seed phrase
 */
const createWallet = async () => {
  try {
    // If we already have a seed phrase, derive a new wallet from it
    let existingSeedPhrase = null;
    if (seedPhraseToken) {
      existingSeedPhrase = getSecureData(seedPhraseToken, true);
    }
    
    if (existingSeedPhrase) {
      // Find the highest address index among existing wallets
      let highestIndex = -1;
      for (const wallet of userWallets) {
        // Extract the index from the path if available
        if (wallet.path) {
          // MetaMask path pattern: m/44'/60'/0'/0/X where X is the address index
          const match = wallet.path.match(/m\/44'\/60'\/0'\/0\/(\d+)$/);
          if (match && match[1]) {
            const index = parseInt(match[1], 10);
            if (index > highestIndex) {
              highestIndex = index;
            }
          } else if (wallet.index !== undefined && wallet.index > highestIndex) {
            // Fallback to stored index if path doesn't match pattern
            highestIndex = wallet.index;
          }
        } else if (wallet.index !== undefined && wallet.index > highestIndex) {
          // Fallback to stored index if no path available
          highestIndex = wallet.index;
        }
      }
      
      // Generate next index and derive the wallet
      const nextIndex = highestIndex + 1;
      console.log(`Deriving new wallet from existing seed phrase with address index ${nextIndex}`);
      
      // Use MetaMask-compatible path: m/44'/60'/0'/0/X
      const path = `m/44'/60'/0'/0/${nextIndex}`;
      const hdNode = ethers.HDNodeWallet.fromMnemonic(
        ethers.Mnemonic.fromPhrase(existingSeedPhrase),
        path
      );
      
      // Create wallet object with the derived data
      return {
        wallet: {
          address: hdNode.address,
          privateKey: hdNode.privateKey,
          path: path,
          index: nextIndex,
          name: t('wallet.account', { number: nextIndex + 1 }) // Translated user-friendly name
        },
        seedPhrase: existingSeedPhrase
      };
    } 
    // If no seed phrase exists, generate one and create the first wallet
    else {
      console.log("Generating new seed phrase and first wallet");
      
      // First generate a random wallet with mnemonic
      const randomWallet = ethers.Wallet.createRandom();
      
      // Extract the mnemonic phrase
      const newSeedPhrase = randomWallet.mnemonic.phrase;
      
      // SECURITY IMPROVEMENT: Securely store the seed phrase
      secureStoreSeedPhrase(newSeedPhrase);
      
      // Encrypt and save if authenticated
      if (isAuthenticated) {
        try {
          const effectivePassword = await getEffectivePassword();
          const encryptedSeedPhrase = await encryptData(newSeedPhrase, effectivePassword);
          localStorage.setItem(ENCRYPTED_SEED_PHRASE_KEY, encryptedSeedPhrase);
          console.log("New seed phrase encrypted and saved");
        } catch (error) {
          console.error("Failed to save seed phrase:", error);
        }
      }
      
      // Create wallet object from the first account (index 0)
      return {
        wallet: {
          address: randomWallet.address,
          privateKey: randomWallet.privateKey,
          path: "m/44'/60'/0'/0/0", // MetaMask-compatible path for first account
          index: 0,
          name: t('wallet.account', { number: 1 }) // Translated first account name
        },
        seedPhrase: newSeedPhrase
      };
    }
  } catch (error) {
    console.error("Error creating wallet:", error);
    throw new Error(t('wallet.errors.createFailed', { message: error.message }));
  }
};

/**
 * Import wallet from seed phrase or private key
 * @param {string} input - Seed phrase or private key to import
 * @returns {Promise<Object>} - Imported wallet object and seed phrase if applicable
 */
const importWallet = async (input) => {
  try {
    // Clean input
    const cleanedInput = input.trim();

    // Check if input is a private key (for backward compatibility)
    if (isPrivateKey(cleanedInput)) {
      console.log("Importing from private key for backward compatibility");

      // Normalize the key (lowercase, trim spaces)
      let normalizedKey = cleanedInput.toLowerCase();
      
      // If it doesn't start with '0x', add it
      if (!normalizedKey.startsWith('0x')) {
        normalizedKey = '0x' + normalizedKey;
      }
      
      // Create wallet via Ethers
      const w = new EthersWallet(normalizedKey);
      
      return {
        wallet: {
          address: w.address,
          privateKey: normalizedKey,
          path: null, // No path for directly imported keys
          index: -1,  // Use -1 to indicate it's not from a seed phrase
          name: t('wallet.importedAccount', { number: userWallets.length + 1 })
        },
        seedPhrase: null // No seed phrase for private key imports
      };
    } 
    // Otherwise, assume it's a seed phrase
    else {
      console.log("Importing from seed phrase");
      
      // Validate the mnemonic
      if (!validateMnemonic(cleanedInput)) {
        throw new Error(t('wallet.errors.invalidSeedPhrase'));
      }
      
      // Create a wallet from the mnemonic (first account)
      // Using MetaMask-compatible path: m/44'/60'/0'/0/0
      const hdNode = ethers.HDNodeWallet.fromMnemonic(
        ethers.Mnemonic.fromPhrase(cleanedInput),
        "m/44'/60'/0'/0/0"
      );
      
      // SECURITY IMPROVEMENT: Securely store the seed phrase
      secureStoreSeedPhrase(cleanedInput);
      
      // Encrypt and save the seed phrase if we're authenticated
      if (isAuthenticated) {
        try {
          const effectivePassword = await getEffectivePassword();
          const encryptedSeedPhrase = await encryptData(cleanedInput, effectivePassword);
          localStorage.setItem(ENCRYPTED_SEED_PHRASE_KEY, encryptedSeedPhrase);
          console.log("Imported seed phrase encrypted and saved");
        } catch (error) {
          console.error("Failed to save imported seed phrase:", error);
          // Continue anyway - we'll save it later when the wallet is saved
        }
      }
      
      return {
        wallet: {
          address: hdNode.address,
          privateKey: hdNode.privateKey,
          path: "m/44'/60'/0'/0/0", // MetaMask-compatible path
          index: 0,
          name: t('wallet.account', { number: 1 })
        },
        seedPhrase: cleanedInput
      };
    }
  } catch (error) {
    console.error("Error importing wallet:", error);
    throw new Error(t('wallet.errors.importFailed', { message: error.message }));
  }
};
  
/**
 * Derive a new wallet from the existing seed phrase
 * @returns {Promise<Object>} - New wallet derived from seed phrase
 */
const deriveNextWallet = async () => {
  // SECURITY IMPROVEMENT: Get the seed phrase securely
  const seedPhrase = seedPhraseToken ? getSecureData(seedPhraseToken, true) : null;
  
  if (!seedPhrase) {
    throw new Error(t('wallet.errors.noSeedPhrase'));
  }
  
  try {
    // Find the highest address index among existing wallets
    let highestIndex = -1;
    for (const wallet of userWallets) {
      // Extract the index from the path if available
      if (wallet.path) {
        // MetaMask path pattern: m/44'/60'/0'/0/X where X is the address index
        const match = wallet.path.match(/m\/44'\/60'\/0'\/0\/(\d+)$/);
        if (match && match[1]) {
          const index = parseInt(match[1], 10);
          if (index > highestIndex) {
            highestIndex = index;
          }
        } else if (wallet.index !== undefined && wallet.index > highestIndex) {
          // Fallback to stored index if path doesn't match pattern
          highestIndex = wallet.index;
        }
      } else if (wallet.index !== undefined && wallet.index > highestIndex) {
        // Fallback to stored index if no path available
        highestIndex = wallet.index;
      }
    }
    
    // Derive the next wallet with MetaMask-compatible derivation path
    const nextIndex = highestIndex + 1;
    console.log(`Deriving new wallet with MetaMask-compatible path using index ${nextIndex}`);
    
    // Use the getWalletFromMnemonic utility with updated path pattern
    const wallet = getWalletFromMnemonic(seedPhrase, null, nextIndex);
    
    return {
      address: wallet.address,
      privateKey: wallet.privateKey,
      path: wallet.path,
      index: nextIndex,
      name: t('wallet.account', { number: nextIndex + 1 }) // Translated user-friendly name
    };
  } catch (error) {
    console.error("Error deriving next wallet:", error);
    throw new Error(t('wallet.errors.deriveFailed', { message: error.message }));
  }
};
  
/**
 * SECURITY IMPROVEMENT: Get the seed phrase for on-demand secure display
 * Instead of returning the raw seed phrase, returns a function that will
 * provide temporary access for a specific operation
 * @returns {Object|null} - Object with access method or null if not available
 */
const getSeedPhrase = () => {
  if (!seedPhraseToken) return null;
  
  try {
    // Create a temporary access to the seed phrase that will be valid for 2 minutes
    const seedPhrase = getSecureData(seedPhraseToken, true, 2 * 60 * 1000);
    
    if (!seedPhrase) {
      console.warn("Seed phrase token is invalid or expired");
      return null;
    }
    
    // Create a one-time access to the seed phrase
    const { access, destroy } = createTemporaryAccess(seedPhrase, 2 * 60 * 1000);
    
    return access();
  } catch (error) {
    console.error("Error getting seed phrase:", error);
    return null;
  }
};
  
  /**
   * Check if a wallet was derived from the current seed phrase
   * @param {Object} wallet - Wallet object to check
   * @returns {boolean} - True if wallet was derived from current seed phrase
   */
  const isWalletFromSeedPhrase = (wallet) => {
    // If we don't have a seed phrase or wallet doesn't have an index, it's not from seed phrase
    if (!seedPhraseToken || wallet.index === undefined || wallet.index < 0) {
      return false;
    }
    
    try {
      // Get the seed phrase securely
      const seedPhrase = getSecureData(seedPhraseToken, true);
      if (!seedPhrase) return false;
      
      // Try to derive the wallet at this index and check if private keys match
      const derivedWallet = getWalletFromMnemonic(seedPhrase, null, wallet.index);
      return derivedWallet.privateKey === wallet.privateKey;
    } catch (error) {
      console.error("Error checking if wallet is from seed phrase:", error);
      return false;
    }
  };
/**
 * Force load seed phrase directly from storage 
 * This is a last-resort method for when normal methods fail
 * @returns {Promise<string|null>} The seed phrase or null if not found
 */
const forceLoadSeedPhrase = async () => {
  try {
    console.log("Attempting to force-load seed phrase directly from storage");
    
    // Check if seed phrase exists in storage
    const encryptedSeedPhrase = localStorage.getItem(ENCRYPTED_SEED_PHRASE_KEY);
    if (!encryptedSeedPhrase) {
      console.log("No encrypted seed phrase found in storage");
      return null;
    }
    
    // Get password for decryption - we need this regardless of auth state
    let effectivePassword;
    try {
      effectivePassword = await getEffectivePassword();
    } catch (err) {
      console.error("Failed to get effective password:", err);
      return null;
    }
    
    if (!effectivePassword) {
      console.error("No effective password available");
      return null;
    }
    
    // Directly decrypt the seed phrase
    try {
      const decryptedPhrase = await decryptData(encryptedSeedPhrase, effectivePassword);
      if (decryptedPhrase) {
        console.log("Successfully force-loaded seed phrase");
        
        // SECURITY IMPROVEMENT: Store securely
        secureStoreSeedPhrase(decryptedPhrase);
        setHasSeedPhrase(true);
        
        return decryptedPhrase;
      }
    } catch (decryptErr) {
      console.error("Failed to decrypt seed phrase:", decryptErr);
      return null;
    }
    
    return null;
  } catch (error) {
    console.error("Force load seed phrase failed:", error);
    return null;
  }
};

// Clean up on unmount
useEffect(() => {
  return () => {
    clearSensitiveData();
  };
}, []);

  return (
    <WalletContext.Provider
      value={{
        userWallets,
        setUserWallets,
        currentWallet,
        setCurrentWallet,
        provider,
        address,
        createWallet,
        importWallet,
        deriveNextWallet,
        getSeedPhrase,
        isWalletFromSeedPhrase,
        hasSeedPhrase,
        isInitialized,
        isEncrypted,
        isDecrypting,
        decryptionError,
        hasStoredWallets,
        setWalletPassword,
        forceLoadSeedPhrase,
        getEffectivePassword,
        retryWalletDecryption,
        forceEncryptWallets,
        retryWalletDecryption,
        clearDecryptionError
      }}
    >
      {children}
    </WalletContext.Provider>
  );
};